// /app/test/:testId/notes?at=<point or unit code>&q=<question number>[&ch=<chapter id>][&from=result]
// — the Notes-with-Back view (BRIEFS C7.5, design _v2/design/NotesFromQuestion.dc.html).
// `ch` comes with a "Notes reference" button (C11.2): that place's own chapter.
//
// The Notes screen in the full-screen layout with the dark pinned bar "Back to Question N"
// ("Your Chapter Test is waiting for you / Question N of M · Your answers are safe"), no
// menu, no outline; the place is boxed and "I have read it. Back to Question N" follows it.
// From the result page (from=result) the bar says "Back to your result".
// The chapter comes from the question's notes place (test paper or result), so a reload
// of this page also works. Back = the runner at the same question (its answers come fresh
// from the server) or the result at the same card.
// Events: qnotes_open (when shown), qnotes_read (visible time, when leaving), qnotes_back
// (one of the two Back buttons).
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTestPaper, getTestResult } from '../../api/v2';
import { track } from '../../components/app/activity';
import { BackBar } from '../../components/app/NotesBack';
import { resultPosKey, testPosKey } from '../../components/app/testParts';
import { C } from '../../components/app/theme';
import { NotesScreen } from './AppNotes';
import { LoadError, Loading } from '../../components/app/LoadState';

const MODE_NAME = { v2_chapter: 'Chapter Test', v2_practice: 'Practice Test', v2_revision: 'Revision' };

// qnotes_open once the view is shown; qnotes_read with the time it was visible.
// Also used by the Revision notes view (C8.1; info may carry one_liner_id).
export function useQnotesTracking(info) {
  const key = info ? `${info.test_id}|${info.question_id || info.one_liner_id || ''}|${info.at || ''}` : '';
  const infoRef = useRef(info);
  infoRef.current = info;
  useEffect(() => {
    const i = infoRef.current;
    if (!i) return undefined;
    const base = {
      test_id: i.test_id, question_id: i.question_id || undefined,
      one_liner_id: i.one_liner_id || undefined, chapter_id: i.chapter_id,
      point_code: i.point_code || undefined, section_code: i.section_code || undefined,
    };
    track('qnotes_open', { ...base, details: { number: i.number, from: i.from, level: i.level } });
    let acc = 0;
    let since = document.visibilityState === 'hidden' ? null : Date.now();
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (since != null) { acc += Date.now() - since; since = null; }
      } else if (since == null) {
        since = Date.now();
      }
    };
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      const ms = acc + (since != null ? Date.now() - since : 0);
      track('qnotes_read', { ...base, duration_ms: Math.round(ms), details: { number: i.number, from: i.from } });
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', send);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', send);
      send();
    };
  }, [key]);
}

export default function AppTestNotes() {
  const { testId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const at = params.get('at') || null;
  const number = Math.max(1, parseInt(params.get('q'), 10) || 1);
  const fromResult = params.get('from') === 'result';

  const dataQ = useQuery({
    queryKey: fromResult ? ['v2', 'test-result', testId] : ['v2', 'test', testId],
    queryFn: () => (fromResult ? getTestResult(testId) : getTestPaper(testId)),
    refetchOnWindowFocus: false,
    // the runner keeps its answers in its own copy: load the paper fresh to see the answer
    // just given (a finished result does not change)
    refetchOnMount: fromResult ? true : 'always',
    retry: (n, err) => !(err && err.response && [404, 409].includes(err.response.status)) && n < 2,
  });
  const ready = fromResult ? Boolean(dataQ.data) : dataQ.isFetchedAfterMount;
  const data = ready ? dataQ.data : undefined;
  const questions = (data && data.questions) || [];
  const q = questions[number - 1] || null;
  const target = q ? (fromResult ? q.notes_target : (q.revealed && q.revealed.notes_target)) : null;
  const chParam = params.get('ch');
  const chapterId = (chParam && /^\d+$/.test(chParam) ? Number(chParam) : null) || (target && target.chapter_id);
  const place = at || (target && (target.point_code || target.section_code)) || null;
  // what was opened (for the events): the place in the address, else the question's own place
  const opened = at ? {
    point_code: /-P\d+$/.test(at) ? at : undefined,
    section_code: at.replace(/-P\d+$/, ''),
    level: /-P\d+$/.test(at) ? 'point' : 'unit',
  } : (target ? { point_code: target.point_code, section_code: target.section_code, level: target.level } : {});

  const goBack = useCallback((button) => {
    track('qnotes_back', {
      test_id: testId, question_id: q ? q.question_id : undefined,
      details: { number, from: fromResult ? 'result' : 'test', button },
    });
    let to;
    if (fromResult) {
      try { sessionStorage.setItem(resultPosKey(testId), String(number)); } catch { /* ignore */ }
      to = `/app/test/${testId}/result`;
    } else {
      try { localStorage.setItem(testPosKey(testId), String(number - 1)); } catch { /* ignore */ }
      // the runner loads the paper again anyway; drop the cached copy too
      qc.removeQueries({ queryKey: ['v2', 'test', testId] });
      to = `/app/test/${testId}`;
    }
    // Came here from that page → step back in the history (so the browser Back button does
    // not bring the notes again); opened directly (reload, typed) → replace this page.
    if (location.state && location.state.v2back && window.history.length > 1) navigate(-1);
    else navigate(to, { replace: true });
  }, [testId, q, number, fromResult, qc, navigate, location.state]);

  const total = questions.length;
  const modeName = (data && MODE_NAME[data.mode]) || 'Test';
  const qmode = useMemo(() => (fromResult ? {
    number, at: place,
    backLabel: 'Back to your result',
    endLabel: 'I have read it. Back to your result',
    line1: data ? data.title : '',
    line2: total ? `Question ${number} of ${total} · Your result is saved` : '',
    onBack: goBack,
  } : {
    number, at: place,
    backLabel: `Back to Question ${number}`,
    endLabel: `I have read it. Back to Question ${number}`,
    line1: `Your ${modeName} is waiting for you`,
    line2: total ? `Question ${number} of ${total} · Your answers are safe` : 'Your answers are safe',
    onBack: goBack,
  }), [fromResult, number, place, data, total, modeName, goBack]);

  useQnotesTracking(chapterId && q ? {
    test_id: testId, question_id: q.question_id, chapter_id: chapterId, number,
    point_code: opened.point_code, section_code: opened.section_code, level: opened.level,
    at: place, from: fromResult ? 'result' : 'test',
  } : null);

  if (!ready && !dataQ.isError) {
    return (
      <>
        <BackBar qmode={qmode} />
        <Loading style={{ padding: '28px 32px' }} />
      </>
    );
  }
  if (!chapterId) {
    return (
      <>
        <BackBar qmode={qmode} />
        <div data-testid="v2-qn-missing" style={{ padding: '28px 32px', fontSize: 16, color: C.text }}>
          {dataQ.isError
            ? <LoadError onRetry={dataQ.refetch} />
            : 'This place in the notes was not found.'}
        </div>
      </>
    );
  }
  return <NotesScreen key={`${chapterId}|${place || ''}`} chapterId={String(chapterId)} at={place} qmode={qmode} />;
}
